import { useEffect, useState, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowsClockwise, DownloadSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

const ALL = "__ALL__";

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const fmtDt = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

const STATE_STYLES = {
  CONFIRMED: "bg-emerald-100 text-emerald-700",
  OPEN: "bg-amber-100 text-amber-700",
  DELAYED: "bg-indigo-100 text-indigo-700",
  REJECTED: "bg-rose-100 text-rose-700",
};

export default function AdminYkConversions() {
  // Filters
  const [accountId, setAccountId] = useState("");
  const [publisherId, setPublisherId] = useState("");
  const [advertiserId, setAdvertiserId] = useState("");
  const [state, setState] = useState("");
  const [eventType, setEventType] = useState("");
  const [startDate, setStartDate] = useState(daysAgo(7));
  const [endDate, setEndDate] = useState(today());
  const [search, setSearch] = useState("");

  // Filter options
  const [opts, setOpts] = useState({
    accounts: [], publishers: [], advertisers: [],
    states: ["OPEN", "CONFIRMED", "REJECTED", "DELAYED"],
    event_types: ["NEW", "UPDATE"],
  });

  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // Data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({ by_currency: [], count: 0, commission: 0, sales_amount: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    API.get("/admin/yk/filters")
      .then((r) => setOpts((prev) => ({ ...prev, ...(r?.data || {}) })))
      .catch(() => { /* sane defaults already populated */ });
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (accountId) params.account_id = accountId;
      if (publisherId) params.publisher_id = publisherId;
      if (advertiserId) params.advertiser_id = advertiserId;
      if (state) params.state = state;
      if (eventType) params.event_type = eventType;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (search.trim()) params.search = search.trim();
      const { data } = await API.get("/admin/yk/commissions", { params });
      setRows(data?.data || []);
      setTotal(data?.total || 0);
      setTotals(data?.totals || { by_currency: [], count: 0, commission: 0, sales_amount: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load conversions");
    } finally {
      setLoading(false);
    }
  }, [accountId, publisherId, advertiserId, state, eventType, startDate, endDate, search, page]);

  useEffect(() => {
    const t = setTimeout(fetchRows, 250);
    return () => clearTimeout(t);
  }, [fetchRows]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const primaryCurrency = totals.by_currency?.[0]?.currency || "EUR";
  const sym = ({ USD: "$", GBP: "£", EUR: "€" }[primaryCurrency]) || `${primaryCurrency} `;
  const periodCommission = totals.by_currency?.length === 1
    ? totals.by_currency[0].commission
    : totals.commission;
  const periodSales = totals.by_currency?.length === 1
    ? totals.by_currency[0].sales_amount
    : totals.sales_amount;
  const currencyNote = (totals.by_currency?.length || 0) > 1
    ? "mixed currencies — see per-currency breakdown"
    : `in ${primaryCurrency}`;

  const downloadCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (accountId) params.set("account_id", accountId);
      if (publisherId) params.set("publisher_id", publisherId);
      if (advertiserId) params.set("advertiser_id", advertiserId);
      if (state) params.set("state", state);
      if (eventType) params.set("event_type", eventType);
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      if (search.trim()) params.set("search", search.trim());
      const baseURL = API.defaults?.baseURL || "";
      const token = localStorage.getItem("token") || "";
      const resp = await fetch(`${baseURL}/admin/yk/commissions/export.csv?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `yieldkit-conversions-${startDate}-to-${endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="admin-yk-conversions-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 font-bold tracking-[0.2em] uppercase">YIELDKIT</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Conversion Report</h1>
          <p className="text-sm text-slate-500 mt-1">
            All commissions/conversions ingested from Yieldkit (postback + scheduled syncs). Publisher is set from the YK account assignment.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={downloadCsv}
            disabled={exporting || total === 0}
            data-testid="yk-conv-export"
          >
            <DownloadSimple size={14} className={`mr-1.5 ${exporting ? "animate-pulse" : ""}`} />
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchRows} data-testid="yk-conv-refresh">
            <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Filter bar — first row: dates + dropdowns, second row: search.
          A single 8-column grid squeezes the date inputs on most laptops; we
          split into two rows so every control has room to breathe. */}
      <div
        className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3"
        data-testid="yk-conv-filters"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Start date</Label>
            <Input
              type="date" value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              data-testid="yk-conv-start"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">End date</Label>
            <Input
              type="date" value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              data-testid="yk-conv-end"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Publisher</Label>
            <Select
              value={publisherId || ALL}
              onValueChange={(v) => { setPublisherId(v === ALL ? "" : v); setPage(1); }}
            >
              <SelectTrigger data-testid="yk-conv-publisher">
                <SelectValue placeholder="All publishers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All publishers</SelectItem>
                {opts.publishers.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Offer</Label>
            <Select
              value={advertiserId || ALL}
              onValueChange={(v) => { setAdvertiserId(v === ALL ? "" : v); setPage(1); }}
            >
              <SelectTrigger data-testid="yk-conv-offer">
                <SelectValue placeholder="All offers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All offers</SelectItem>
                {opts.advertisers.map((a) => (
                  <SelectItem key={a.advertiser_id} value={a.advertiser_id}>
                    {a.name || a.advertiser_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">YK Account</Label>
            <Select
              value={accountId || ALL}
              onValueChange={(v) => { setAccountId(v === ALL ? "" : v); setPage(1); }}
            >
              <SelectTrigger data-testid="yk-conv-account">
                <SelectValue placeholder="All accounts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All accounts</SelectItem>
                {opts.accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.label || a.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Status</Label>
            <Select
              value={state || ALL}
              onValueChange={(v) => { setState(v === ALL ? "" : v); setPage(1); }}
            >
              <SelectTrigger data-testid="yk-conv-state">
                <SelectValue placeholder="All states" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All states</SelectItem>
                {(opts.states || []).map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Event Type</Label>
            <Select
              value={eventType || ALL}
              onValueChange={(v) => { setEventType(v === ALL ? "" : v); setPage(1); }}
            >
              <SelectTrigger data-testid="yk-conv-event-type">
                <SelectValue placeholder="All events" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All events</SelectItem>
                {(opts.event_types || []).map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Search</Label>
          <div className="relative max-w-md">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="event ID / commission ID / sub_id"
              className="pl-8"
              data-testid="yk-conv-search"
            />
          </div>
        </div>
      </div>

      {/* Period totals for the full filtered window */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm" data-testid="yk-conv-rollup">
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Conversions (period)</div>
          <div className="font-heading text-xl font-bold text-slate-900 mt-1">{total.toLocaleString()}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">
            {startDate} → {endDate}
          </div>
        </div>
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Commission (period)</div>
          <div className="font-heading text-xl font-bold text-[#002FA7] mt-1">{sym}{Number(periodCommission || 0).toFixed(2)}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">{currencyNote}</div>
        </div>
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Sales Amount (period)</div>
          <div className="font-heading text-xl font-bold text-slate-900 mt-1">{sym}{Number(periodSales || 0).toFixed(2)}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">{currencyNote}</div>
        </div>
      </div>
      {(totals.by_currency?.length || 0) > 1 ? (
        <div className="flex flex-wrap gap-2 text-[11px] text-slate-600" data-testid="yk-conv-currency-breakdown">
          {totals.by_currency.map((bucket) => {
            const bSym = ({ USD: "$", GBP: "£", EUR: "€" }[bucket.currency]) || `${bucket.currency} `;
            return (
              <span key={bucket.currency} className="rounded border border-slate-200 bg-white px-2 py-1">
                {bucket.currency}: {bSym}{Number(bucket.commission || 0).toFixed(2)} commission · {bSym}{Number(bucket.sales_amount || 0).toFixed(2)} sales ({bucket.count} rows)
              </span>
            );
          })}
        </div>
      ) : null}

      {/* Table */}
      <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Sale Date</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Modified</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Publisher</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Offer</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Event ID</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Commission ID</th>
              <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Sales</th>
              <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Commission</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Event</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Status</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Account</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="px-4 py-12 text-center text-slate-400 text-sm">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-12 text-center text-slate-500 text-sm">No conversions for the selected filters.</td></tr>
            )}
            {rows.map((r) => {
              const cur = (r.currency || "EUR").toUpperCase();
              const cSym = ({ USD: "$", GBP: "£", EUR: "€" }[cur]) || cur + " ";
              return (
              <tr key={`${r.account_id}-${r.event_id}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`yk-conv-row-${r.event_id}`}>
                <td className="px-3 py-2 text-[11px] text-slate-700 whitespace-nowrap">{fmtDt(r.sales_date)}</td>
                <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap">{fmtDt(r.modified_date)}</td>
                <td className="px-3 py-2 text-[11px] text-slate-700">
                  {r.publisher_email || (
                    <span className="font-mono text-[10px] text-slate-400">{r.publisher_id || "—"}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-[11px]">
                  <div className="font-semibold text-slate-800">{r.advertiser_name || "—"}</div>
                </td>
                <td className="px-3 py-2 text-[11px] font-mono text-slate-600">{r.event_id}</td>
                <td className="px-3 py-2 text-[11px] font-mono text-slate-500">{r.commission_id || "—"}</td>
                <td className="px-3 py-2 text-[11px] font-mono text-right text-slate-700">
                  {cSym}{Number(r.sales_amount_eur || 0).toFixed(2)}
                </td>
                <td className="px-3 py-2 text-[11px] font-mono text-right text-[#002FA7] font-bold">
                  {cSym}{Number(r.commission_eur || 0).toFixed(2)}
                </td>
                <td className="px-3 py-2 text-[10px] text-slate-600">{r.event_type || "—"}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${STATE_STYLES[r.state] || "bg-slate-100 text-slate-600"}`}>
                    {r.state || "—"}
                  </span>
                </td>
                <td className="px-3 py-2 text-[11px] text-slate-500">{r.account_label || "—"}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <div>
          {total.toLocaleString()} conversion{total === 1 ? "" : "s"} · page <span className="font-mono font-bold">{page}</span> / <span className="font-mono">{totalPages}</span>
        </div>
        <div className="flex gap-1.5">
          <Button
            variant="outline" size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage(page - 1)}
            data-testid="yk-conv-prev"
          >Prev</Button>
          <Button
            variant="outline" size="sm"
            disabled={loading || page >= totalPages}
            onClick={() => setPage(page + 1)}
            data-testid="yk-conv-next"
          >Next</Button>
        </div>
      </div>
    </div>
  );
}
